import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config';
// Membership features deprecated; keep only payments/subscriptions utilities used elsewhere
import type { Payment, Subscription } from './types';

// createMembershipTier removed

export async function getUserMembershipTiers(_userId: string): Promise<any[]> { return []; }

// updateMembershipTier removed

export async function checkExistingSubscription(userId: string, membershipId: string): Promise<boolean> {
  if (!userId || !membershipId) {
    throw new Error('User ID and membership ID are required');
  }

  const subscriptionQuery = query(
    collection(db, 'subscriptions'),
    where('userId', '==', userId),
    where('membershipId', '==', membershipId),
    where('status', 'in', ['active', 'past_due'])
  );

  const snapshot = await getDocs(subscriptionQuery);
  return !snapshot.empty;
}

export async function checkCreatorPaymentSetup(creatorId: string): Promise<boolean> {
  if (!creatorId) {
    throw new Error('Creator ID is required');
  }

  const creatorDoc = await getDoc(doc(db, 'users', creatorId));

  if (!creatorDoc.exists()) {
    throw new Error('Creator not found');
  }

  const creator = creatorDoc.data();
  return Boolean(creator.stripeAccountId && creator.stripeAccountVerified);
}

// Get the base URL for redirects
export const getBaseUrl = () => {
  // Add debug logging
  console.log('Environment check:', {
    isDev: import.meta.env.DEV,
    mode: import.meta.env.MODE,
    baseUrl: import.meta.env.VITE_APP_URL
  });

  if (import.meta.env.DEV) {
    console.log('Using development URL: http://localhost:5173');
    return 'http://localhost:5173';
  }
  console.log('Using production URL:', import.meta.env.VITE_APP_URL);
  return import.meta.env.VITE_APP_URL;
};

export const startSubscription = async (_userId: string, _membershipId: string) => {
  console.log('startSubscription is deprecated');
  throw new Error('Membership flow is deprecated');
};

export async function getActiveSubscriptions(creatorId: string): Promise<Subscription[]> {
  try {
    const q = query(
      collection(db, 'subscriptions'),
      where('creatorId', '==', creatorId),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Subscription[];
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch subscriptions');
  }
}

export async function getCreatorPayments(creatorId: string): Promise<Payment[]> {
  try {
    const q = query(
      collection(db, 'payments'),
      where('creatorId', '==', creatorId),
      where('status', '==', 'succeeded'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Payment[];
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch payments');
  }
}

export async function migrateExistingMemberships(): Promise<void> { return; }

export const createFreeSubscription = async (userId: string, creatorId: string) => {
  console.log('Creating free subscription with:', { userId, creatorId });

  if (!userId || !creatorId) {
    console.error('Missing required parameters:', { userId, creatorId });
    throw new Error('User ID and creator ID are required');
  }

  // Check if user is trying to subscribe to themselves
  if (userId === creatorId) {
    console.error('User trying to subscribe to themselves:', { userId, creatorId });
    throw new Error('You cannot subscribe to your own mission page');
  }

  // Check for existing subscription
  console.log('Checking for existing subscription');
  const q = query(
    collection(db, 'subscriptions'),
    where('userId', '==', userId),
    where('creatorId', '==', creatorId),
    where('status', '==', 'active')
  );

  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    console.log('Found existing subscription - preventing duplicate');
    throw new Error('You are already subscribed to this mission page');
  }

  // Create free subscription record
  console.log('Creating free subscription record');
  const subscriptionRef = await addDoc(collection(db, 'subscriptions'), {
    userId,
    creatorId,
    membershipId: null,
    status: 'active',
    stripeSubscriptionId: null,
    amount: 0,
    subscriptionType: 'free',
    currentPeriodStart: new Date(),
    currentPeriodEnd: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    cancelAtPeriodEnd: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  console.log('Created free subscription:', subscriptionRef.id);
  return subscriptionRef.id;
};